// src/screens/ForgotPasswordScreen.js
// The Forgot page (FORGOT_PASSWORD_DESIGN.md 4.2; strings FP1 to FP12):
// critcash.com/forgot-password, reached from the Login screen's "Forgot
// password?" link (the typed address comes over in memory, never as a route
// param) or typed directly. No sign-in needed.
//
// "Send link" calls sendPasswordReset({email}). The server answers every
// address the same way once its limits pass, so the page shows FP5 ("If that
// address has a CritCast account, we sent it an email...") for every
// success and never says whether an account exists or how it signs in.
// Refusals: an invalid address (FP8, under the field), the server's limit and
// retry sentences as written (FP9 to FP11), anything else FP12. Editing the
// address clears every earlier result (review UX-2). The call asks for a
// limited-use App Check token: the server consumes each token once. When App
// Check cannot attest (the SDK then sends nothing), or the call has not
// answered in 15 s and the reCAPTCHA library has not loaded yet (so nothing
// has been sent yet), the page tries once more without App Check, through a
// second Firebase app that has none (review FP-R2-1); the server counts such
// calls on its no-App-Check limits. With the library loaded, a slow call is
// awaited, not sent twice (review R3-01). If the library loads after the
// 15 s mark, the first call still goes out once its token comes: a second
// set of counter slots and possibly a second email (review R4-02).

import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { initializeApp, getApps } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, functions } from '../../firebaseConfig';
import Footer from '../components/Footer';
import {
  FORGOT_TITLE,
  FORGOT_LEAD,
  EMAIL_LABEL,
  SEND_LINK_LABEL,
  FORGOT_SENT_LINE,
  SEND_AGAIN_LABEL,
  BACK_TO_SIGN_IN_LABEL,
  INVALID_EMAIL_LINE,
  MAX_EMAIL_LENGTH,
  parseEmail,
  forgotErrorLine,
  sendResetRequest,
  peekResetPrefill,
  clearResetPrefill,
} from '../utils/passwordReset';

// The same Firebase project as a second app with NO App Check (firebaseConfig.js
// sets App Check up on the default app only). Made on first use, only when the
// call with App Check could not be made (review FP-R2-1).
const NO_APP_CHECK_APP = 'noAppCheck';
function functionsWithoutAppCheck() {
  return getFunctions(getApps().find((a) => a.name === NO_APP_CHECK_APP) || initializeApp(app.options, NO_APP_CHECK_APP));
}

const COLORS = {
  BG: '#000',
  CARD: '#111',
  BORDER: '#222',
  GOLD: '#FCD000',
  GREEN: '#2ECC71',
  TEXT: '#EDEDED',
  DIM: '#aaa',
  RED: '#e05555',
  FIELD: '#1b1b1b',
};

export default function ForgotPasswordScreen({ navigation }) {
  const [email, setEmail] = useState(() => peekResetPrefill());
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [error, setError] = useState('');
  const sendingRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    clearResetPrefill();
    return () => { aliveRef.current = false; };
  }, []);

  // Back where the visitor came from when that is Login or Change Password
  // (review R2-05); from anywhere else (a direct visit, or AuthCallback, whose
  // route params would put a spent OAuth code back in the address bar; review
  // R3-03) the stack becomes a lone Login, so Login's own Back link has no
  // AuthCallback below it to return to (review R4-01: a replace kept it there).
  const backToSignIn = () => {
    const s = navigation.getState();
    const prev = s?.routes?.[s.index - 1]?.name;
    if (prev === 'Login' || prev === 'ChangePassword') navigation.goBack();
    else navigation.reset({ index: 0, routes: [{ name: 'Login' }] });
  };

  const submit = async () => {
    if (sendingRef.current) return;
    setError('');
    setSent(false);
    const address = parseEmail(email);
    if (!address) {
      setFieldError(INVALID_EMAIL_LINE);
      return;
    }
    setFieldError('');
    sendingRef.current = true;
    setSending(true);
    try {
      await sendResetRequest({
        withAppCheck: () => httpsCallable(functions, 'sendPasswordReset', { limitedUseAppCheckTokens: true })({ email: address }),
        withoutAppCheck: () => httpsCallable(functionsWithoutAppCheck(), 'sendPasswordReset')({ email: address }),
      });
      if (aliveRef.current) setSent(true);
    } catch (err) {
      console.warn('[forgot-password] sendPasswordReset failed:', err?.code || '', err?.message || '');
      if (!aliveRef.current) return;
      const line = forgotErrorLine(err);
      if (line.field) setFieldError(line.text);
      else setError(line.text);
    } finally {
      sendingRef.current = false;
      if (aliveRef.current) setSending(false);
    }
  };

  return (
    <View style={styles.outer}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <View style={styles.card}>
          <Text style={styles.title} accessibilityRole="header">{FORGOT_TITLE}</Text>
          <Text style={styles.body}>{FORGOT_LEAD}</Text>

          <Text style={styles.label}>{EMAIL_LABEL}</Text>
          <TextInput
            style={[styles.input, fieldError ? styles.inputErr : null]}
            value={email}
            onChangeText={(t) => { setEmail(t); setFieldError(''); setSent(false); setError(''); }}
            onSubmitEditing={submit}
            accessibilityLabel={EMAIL_LABEL}
            autoComplete="email"
            textContentType="emailAddress"
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            autoFocus
            returnKeyType="send"
            maxLength={MAX_EMAIL_LENGTH}
            editable={!sending}
          />
          <View role="alert">
            {fieldError ? <Text style={styles.fieldErr}>{fieldError}</Text> : null}
          </View>

          <TouchableOpacity
            style={[styles.goldBtn, sending ? styles.btnBusy : null]}
            onPress={submit}
            disabled={sending}
            accessibilityRole="button"
            accessibilityState={{ disabled: sending, busy: sending }}
          >
            {sending ? <ActivityIndicator color="#0b0b0b" style={styles.btnSpinner} /> : null}
            <Text style={styles.goldBtnText}>{sent ? SEND_AGAIN_LABEL : SEND_LINK_LABEL}</Text>
          </TouchableOpacity>

          <View role="status">
            {sent ? <Text style={[styles.body, styles.ok]}>{FORGOT_SENT_LINE}</Text> : null}
          </View>
          <View role="alert">
            {error ? <Text style={[styles.body, styles.err]}>{error}</Text> : null}
          </View>

          {/* Back to Login, or to Change Password when signed in (reviews
              R2-05, R3-03). */}
          <TouchableOpacity
            style={styles.linkBtn}
            onPress={backToSignIn}
            accessibilityRole="link"
          >
            <Text style={styles.link}>{BACK_TO_SIGN_IN_LABEL}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
      <Footer navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  outer: { flex: 1, backgroundColor: COLORS.BG },
  scroll: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  card: {
    width: '100%', maxWidth: 440, backgroundColor: COLORS.CARD, borderColor: COLORS.BORDER,
    borderWidth: 1, borderRadius: 14, padding: 22,
  },
  title: { color: COLORS.GOLD, fontSize: 22, fontWeight: '800', textAlign: 'center', marginBottom: 12 },
  body: { color: COLORS.TEXT, fontSize: 14.5, lineHeight: 21, textAlign: 'center', marginTop: 4 },
  ok: { color: COLORS.GREEN, fontWeight: '700', marginTop: 14 },
  err: { color: COLORS.RED, marginTop: 14 },
  label: { color: COLORS.TEXT, fontSize: 13.5, fontWeight: '700', marginTop: 18, marginBottom: 6 },
  input: {
    backgroundColor: COLORS.FIELD, color: '#fff', borderColor: COLORS.BORDER, borderWidth: 1, borderRadius: 8,
    paddingHorizontal: 12, paddingVertical: 10, fontSize: 15, minHeight: 44,
  },
  inputErr: { borderColor: COLORS.RED },
  fieldErr: { color: COLORS.RED, fontSize: 13, marginTop: 6 },
  goldBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 10, paddingVertical: 12, minHeight: 44, marginTop: 18,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
  },
  btnBusy: { opacity: 0.75 },
  btnSpinner: { marginRight: 8 },
  goldBtnText: { color: '#0b0b0b', fontSize: 14, fontWeight: '800' },
  // The link alone is the tap area (44 px tall, 8 px sides), not the card's
  // full width (review R2-07, as MOB-2 on Login).
  linkBtn: { minHeight: 44, alignSelf: 'center', paddingHorizontal: 8, alignItems: 'center', justifyContent: 'center', marginTop: 10 },
  link: { color: COLORS.GOLD, fontSize: 14, textDecorationLine: 'underline' },
});
